import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION_NAME = 'metrics';

// Create a new metric
export const createMetric = async (metricData, userId) => {
  try {
    console.log('Creating metric with data:', metricData);
    console.log('User ID:', userId);
    
    // Get the current highest order value for this user
    const existingMetrics = await getMetrics(userId);
    const maxOrder = existingMetrics.length > 0 ? Math.max(...existingMetrics.map(m => m.order || 0)) : 0;
    
    const docData = {
      ...metricData,
      userId,
      order: maxOrder + 1, // Set order to be after the last metric
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    console.log('Document data to be saved:', docData);
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), docData);
    return { id: docRef.id, ...metricData, order: maxOrder + 1 };
  } catch (error) {
    console.error('Error creating metric:', error);
    console.error('Error details:', error.message, error.code);
    throw new Error(`Failed to create metric: ${error.message}`);
  }
};

// Get all metrics for a user
export const getMetrics = async (userId) => {
  try {
    // First try to get metrics ordered by order field
    let q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('order', 'asc')
    );
    
    try {
      const querySnapshot = await getDocs(q);
      const metrics = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore timestamps to ISO strings for consistency
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      }));
      
      // Check if any metrics are missing the order field
      const needsMigration = metrics.some(metric => !metric.order);
      
      if (needsMigration) {
        console.log('Migrating metrics to include order field...');
        // Update metrics without order field
        const migrationPromises = metrics.map(async (metric, index) => {
          if (!metric.order) {
            await updateMetricOrder(metric.id, index + 1, userId);
            return { ...metric, order: index + 1 };
          }
          return metric;
        });
        
        await Promise.all(migrationPromises);
        // Return metrics with updated order
        return metrics.map((metric, index) => ({
          ...metric,
          order: metric.order || index + 1
        }));
      }
      
      return metrics;
    } catch (orderError) {
      // If orderBy fails, fall back to createdAt ordering
      console.log('Order field not available, falling back to createdAt ordering');
      q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const metrics = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore timestamps to ISO strings for consistency
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      }));
      
      // Add order field to existing metrics
      const metricsWithOrder = metrics.map((metric, index) => ({
        ...metric,
        order: metric.order || index + 1
      }));
      
      // Update metrics in Firestore with order field
      const updatePromises = metricsWithOrder.map(metric => 
        updateMetricOrder(metric.id, metric.order, userId)
      );
      
      await Promise.all(updatePromises);
      
      return metricsWithOrder;
    }
  } catch (error) {
    console.error('Error fetching metrics:', error);
    console.error('Error details:', error.message, error.code);
    throw new Error(`Failed to fetch metrics: ${error.message}`);
  }
};

// Update an existing metric
export const updateMetric = async (metricId, updateData, userId) => {
  try {
    const metricRef = doc(db, COLLECTION_NAME, metricId);
    await updateDoc(metricRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return { id: metricId, ...updateData };
  } catch (error) {
    console.error('Error updating metric:', error);
    console.error('Error details:', error.message, error.code);
    throw new Error(`Failed to update metric: ${error.message}`);
  }
};

// Delete a metric
export const deleteMetric = async (metricId, userId) => {
  try {
    // Verify ownership before deleting
    const q = query(
      collection(db, COLLECTION_NAME),
      where('__name__', '==', metricId),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('Metric not found or access denied');
    }
    
    await deleteDoc(doc(db, COLLECTION_NAME, metricId));
    return metricId;
  } catch (error) {
    console.error('Error deleting metric:', error);
    console.error('Error details:', error.message, error.code);
    throw new Error(`Failed to delete metric: ${error.message}`);
  }
};

// Get a single metric by ID
export const getMetric = async (metricId, userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('__name__', '==', metricId),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('Metric not found');
    }
    
    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
    };
  } catch (error) {
    console.error('Error fetching metric:', error);
    console.error('Error details:', error.message, error.code);
    throw new Error(`Failed to fetch metric: ${error.message}`);
  }
};

// Update metric order
export const updateMetricOrder = async (metricId, newOrder, userId) => {
  try {
    const metricRef = doc(db, COLLECTION_NAME, metricId);
    await updateDoc(metricRef, {
      order: newOrder,
      updatedAt: serverTimestamp()
    });
    return { id: metricId, order: newOrder };
  } catch (error) {
    console.error('Error updating metric order:', error);
    throw new Error(`Failed to update metric order: ${error.message}`);
  }
};
